import { Filter, MoleListMeta, AVAILABLE_LIMIT } from 'views/VvsMinerMoleGallery/types'
import { useInfiniteQuery, UseInfiniteQueryOptions, useQueries, useQuery, UseQueryOptions } from 'react-query'
import { isNilOrUndef } from 'utils/isNilOrUndef'
import { QueryKey } from 'config/constants/queryKey'
import { useEffect } from 'react'

export const INFO_API_ENDPOINT = process.env.REACT_APP_INFO_API_ENDPOINT

/**
 *  Get Mole List with params
 *
 *  limit: 10, 25, 50, 100
 *  page: 0, 1, 2, 3, 4, 5...
 *  id: searched id
 *  filter: legendary | ''
 *  sort: id
 *  order: des | asc
 *
 *  ex: ENDPOINT/mole/metas/list?limit=10&page=0&id=&filter=legendary&sort=id&order=des
 */
export const GET_GALLERY_MOLES_LIST = `mole/metas/list`
export const GET_USER_NFT_COLLECTIONS = `nfts`
export const GET_NFT_ASSETS_INFO = `nft`

export const fetchGalleryMolesList = async (
  params: Filter = {
    limit: AVAILABLE_LIMIT['50'],
    page: 0,
    filter: '',
    sort: 'id',
    order: 'asc',
  },
  signal: AbortSignal,
): Promise<MoleListMeta> => {
  const { limit, page, id, filter, sort, order } = params
  const res = await fetch(
    `${INFO_API_ENDPOINT}/${GET_GALLERY_MOLES_LIST}?limit=${limit}&page=${page}${isNilOrUndef(id) ? '' : `&id=${id}`}${
      isNilOrUndef(filter) ? '' : `&filter=${filter}`
    }&sort=${sort}&order=${order}`,
    {
      signal,
    },
  )
  return res.json()
}

export const useListMolesQuery = (
  filter: Filter,
  queryOptions?: Omit<UseQueryOptions<MoleListMeta, unknown, MoleListMeta, any[]>, 'queryFn' | 'queryKey'>,
) => {
  return useQuery(
    [QueryKey.VVS_INFO_API_META_MOLE_LIST, filter],
    ({ signal }) => fetchGalleryMolesList(filter, signal),
    queryOptions,
  )
}

export enum CHAIN_TYPE {
  CRONOS = 'CRONOS',
  ETHEREUM = 'ETHEREUM',
}

interface CollectionFilter {
  chain: CHAIN_TYPE
  collections: string[]
  listingType?: 'ALL'
}

export interface CollectionParams {
  address: string
  first?: number
  sort?: 'LOWEST_PRICE'
  filter?: CollectionFilter
}

interface Image {
  url: string
}

interface Node {
  image: Image
  name: string
  rarityRank: string // TODO: verify this type
  tokenId: string
}
interface EdgeNode {
  cursor: string
  node: Node
}
interface PageInfo {
  endCursor: string
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor: string
}
export interface UserNftCollectionsMeta {
  edges: EdgeNode[]
  totalCount: number
  pageInfo: PageInfo
}

const FetchUserNftCollectionsDefaultParams = {
  first: 50,
  sort: 'LOWEST_PRICE',
  filter: {
    chain: CHAIN_TYPE.CRONOS,
    collections: [],
    listingType: 'ALL',
  },
}

export const fetchUserNftCollections = async (
  params: CollectionParams = {
    address: '',
    first: 50,
    sort: 'LOWEST_PRICE',
    filter: {
      chain: CHAIN_TYPE.CRONOS,
      collections: [],
      listingType: 'ALL',
    },
  },
  signal: AbortSignal,
): Promise<UserNftCollectionsMeta> => {
  const res = await fetch(`${INFO_API_ENDPOINT}/${GET_USER_NFT_COLLECTIONS}`, {
    method: 'POST',
    headers: {
      Accept: 'application.json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...FetchUserNftCollectionsDefaultParams,
      ...params,
      filter: {
        ...FetchUserNftCollectionsDefaultParams.filter,
        ...params?.filter,
      },
    }),
    signal,
  })
  return res.json()
}

export const useUserNftCollections = (
  collectionParams: CollectionParams,
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  return useQuery(
    [QueryKey.VVS_INFO_API_META_USER_NFT_COLLECTIONS, collectionParams],
    ({ signal }) => fetchUserNftCollections(collectionParams, signal),
    queryOptions,
  )
}

export const useUserAllNftCollections = (
  collectionParams: CollectionParams,
  queryOptions?: Partial<Pick<UseInfiniteQueryOptions, 'enabled'>>,
) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery(
    ['test'],
    ({ pageParam, signal }) => fetchUserNftCollections(pageParam ?? collectionParams, signal),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage?.pageInfo?.hasNextPage) {
          return {
            ...collectionParams,
            after: lastPage?.pageInfo?.endCursor,
          }
        }
        return undefined
      },
      ...queryOptions,
    },
  )

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }, [data, fetchNextPage, hasNextPage])

  return {
    data,
    isLoading: isFetching || isFetchingNextPage,
    isFetching,
    isFetchingNextPage,
  }
}

interface FetchNftAssetInfoParams {
  nftAddress: string
  chain: CHAIN_TYPE
  tokenId: string
}
interface NftAssetMeta {
  image: Image
  tokenId: string
  name: string
}

export const fetchNftAssetInfo = async (
  params: FetchNftAssetInfoParams = {
    nftAddress: '',
    chain: CHAIN_TYPE.CRONOS,
    tokenId: '',
  },
): Promise<NftAssetMeta> => {
  const { nftAddress, chain, tokenId } = params
  const res = await fetch(
    `${INFO_API_ENDPOINT}/${GET_NFT_ASSETS_INFO}?address=${nftAddress}&chain=${chain}&id=${tokenId}`,
  )
  return res.json()
}

export const useNftsAssetInfo = (
  nftsAssetParams: Omit<FetchNftAssetInfoParams, 'tokenId'> & { tokenIds: string[] } = {
    nftAddress: '',
    chain: CHAIN_TYPE.CRONOS,
    tokenIds: [],
  },
  queryOptions?: Partial<Pick<UseQueryOptions, 'enabled'>>,
) => {
  const { nftAddress, chain, tokenIds } = nftsAssetParams
  return useQueries(
    tokenIds.map((tokenId) => {
      return {
        queryKey: ['user', tokenId],
        queryFn: () => fetchNftAssetInfo({ nftAddress, tokenId, chain }),
        enabled: queryOptions?.enabled ?? true,
        ...queryOptions,
      }
    }),
  )
}
